import {capitalize} from 'lodash'
import {z} from 'zod'

export enum DeviceStatus {
    unknown = 'unknown',
    trusted = 'trusted',
    rejected = 'rejected',
}

const USB_DEVICE_TYPE = ['storage', 'other'] as const

const UsbDeviceTypeSchema = z.enum(USB_DEVICE_TYPE)

export type UsbDeviceType = z.infer<typeof UsbDeviceTypeSchema>

export enum USBDevicesSortType {
    TIMESTAMP = 'firstSeenTimestamp',
    LATEST_TIMESTAMP = 'lastSeenTimestamp',
    VENDOR = 'vendorName',
    PRODUCT_NAME = 'productName',
    DEVICE_ID = 'deviceId',
    TYPE = 'deviceType',
    NUMBER_OF_HOSTS_PER_ITEM = 'numberOfHostsPerItem',
    NUMBER_OF_VESSELS_PER_ITEM = 'numberOfVesselsPerItem',
}

export function isValidDeviceStatus(value: unknown): value is DeviceStatus {
    return Object.values(DeviceStatus).some((v) => v === value)
}

export function getStatusDisplayText(status: DeviceStatus): string {
    if (status === DeviceStatus.rejected) {
        return 'Unwanted'
    }
    return capitalize(status)
}

export interface UsbHistoryRequest {
    auditType?: string
    pagination?: {
        pageOffset: number
        pageSize: number
    }
}

export enum UsbHistoryEventType {
    FirstSeen = 'usb-inventory.usb-first-seen',
    Enforcement = 'usb-inventory.usb-enforcement',
    StatusChange = 'usb-inventory.usb-status-change',
}

// Data schemas for each event type
export const usbFirstSeenDataSchema = z.object({
    assetName: z.string().optional(),
    locationName: z.string().optional(),
})

export const usbEnforcementDataSchema = z.object({
    action: z.string(),
    reason: z.string().optional(),
    locationName: z.string().optional(),
    assetName: z.string().optional(),
})

export const usbStatusChangeDataSchema = z.object({
    oldStatus: z.string().optional(),
    newStatus: z.string(),
    userName: z.string().optional(),
    locationName: z.string().optional(),
})

const usbHistoryItemSchema = z.object({
    identity: z.string(),
    type: z.string(),
    userId: z.string().nullable(),
    data: z.unknown(),
    createdAt: z.string().nullable(),
})

export const usbHistoryResponseSchema = z.object({
    data: z.array(usbHistoryItemSchema),
})

export type UsbHistoryItem = z.infer<typeof usbHistoryItemSchema>
export type UsbHistoryResponse = z.infer<typeof usbHistoryResponseSchema>

const PaginationSchema = z
    .object({
        pageSize: z.number(),
        pageOffset: z.number(),
    })
    .nullish()

const OrderBySchema = z
    .object({
        column: z.enum(USBDevicesSortType),
        isAscending: z.boolean(),
    })
    .nullish()

const NullishString = z.string().nullish()

const usbDeviceFindRequestSchema = z.object({
    pagination: PaginationSchema,
    deviceStatuses: z.array(z.enum(DeviceStatus)).nullish(),
    fromRelativeFirstSeen: NullishString,
    fromRelativeLastSeen: NullishString,
    vendorName: NullishString,
    productName: NullishString,
    deviceTypes: z.array(UsbDeviceTypeSchema).nullish(),
    hasPurpose: z.boolean().nullish(),
    locations: z.array(z.uuid()).nullish(),
    assets: z.array(z.uuid()).nullish(),
    orderBy: OrderBySchema,
})

export type UsbDeviceFindRequest = z.infer<typeof usbDeviceFindRequestSchema>

const listOfAssociatedAssetsSchema = z.object({
    identity: z.uuid(),
    firstSeenTimestamp: z.iso.datetime(),
    lastSeenTimestamp: z.iso.datetime(),
})

export type ListOfAssociatedAssets = z.infer<typeof listOfAssociatedAssetsSchema>

const usbDeviceVolumeSchema = z.object({
    fileSystem: z.string(),
    identity: z.uuid(),
    label: z.string(),
    serialNumber: z.string(),
    usbDeviceIdentity: z.uuid(),
})

const lastEnforcedActionSchema = z.object({
    assetId: z.uuid(),
    lastSeenAt: z.iso.datetime(),
    action: z.string(),
    reason: z.string(),
})

export type LastEnforcedAction = z.infer<typeof lastEnforcedActionSchema>

const usbDeviceSchema = z.object({
    identity: z.uuid(),
    vendorId: z.string().optional(),
    vendorName: z.string(),
    productId: z.string().optional(),
    productName: z.string().optional(),
    deviceId: z.string(),
    deviceType: UsbDeviceTypeSchema,
    firstSeenTimestamp: z.iso.datetime(),
    lastSeenTimestamp: z.iso.datetime(),
    assets: z.array(listOfAssociatedAssetsSchema).optional(),
    deviceStatus: z.enum(DeviceStatus),
    volumes: z.array(usbDeviceVolumeSchema),
    purpose: z.string().nullable(),
    purposeSubmittedBy: z.string().nullable(),
    purposeSubmittedWhen: z.iso.datetime().nullable(),
    detectedByAgent: z.boolean(),
    serial: z.string(),
    lastEnforcedAction: lastEnforcedActionSchema.nullable(),
})

export const usbDeviceFindResponseSchema = z.object({
    data: z.array(usbDeviceSchema),
    criteria: usbDeviceFindRequestSchema,
    totalNumberOfItems: z.number(),
    totalNumberOfPages: z.number(),
})

export type UsbDevice = z.infer<typeof usbDeviceSchema>

export type UsbDeviceFindResponse = z.infer<typeof usbDeviceFindResponseSchema>
